import { useAuthState } from '../../services/auth';
import { useEffect, useState, useRef } from 'react';
import './UISettings.scss';
import axios from 'axios';
import { useToast } from '../../services/ToastContext';
import '../../design-system/index.css';
import { LoadingSpinner } from '../../components/loading-spinner/LoadingSpinner';
import {
  Activity,
  Baby,
  BadgeCheck,
  Ban,
  BarChart3,
  Bot,
  Briefcase,
  CheckCircle,
  Download,
  Eye,
  EyeOff,
  Filter,
  Hash,
  HelpCircle,
  Info,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  LucideIcon,
  MailPlus,
  Menu,
  MonitorSmartphone,
  PauseCircle,
  PhoneCall,
  RotateCcw,
  Router,
  Settings,
  ShieldAlert,
  ShieldCheck,
  SlidersHorizontal,
  Smartphone,
  Terminal,
  Timer,
  Upload,
  UserCog,
  UserPlus,
  Users,
  Wrench,
  XCircle,
} from 'lucide-react';

type Props = {
  darkmode?: boolean;
};

export const UISettings = (Props: Props) => {
  const { token } = useAuthState();
  const [customerId, setCustomerId] = useState('');
  const [retinaDns, setRetinaDns] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedMenu, setSelectedMenu] = useState<string>('Dashboard');
  const { showToastMessage } = useToast();

  /** ========================
   *  DEVICE + ACCOUNT SETTINGS
   *  ======================== */
  type Settings = {
    passwordManagement: boolean;
    alternateEmail: boolean;
    registration: boolean;
    details: boolean;
    otp: boolean;
    pause: boolean;
    extraTime: boolean;
    metrics: boolean;
    threats: boolean;
    profile: boolean;
    enroll: boolean;
    groups: boolean;
    rules: boolean;
    commands: boolean;
  };

  const [settings, setSettings] = useState<Settings>({
    passwordManagement: true,
    alternateEmail: true,
    registration: true,
    details: true,
    otp: true,
    pause: true,
    extraTime: true,
    metrics: true,
    threats: true,
    profile: true,
    enroll: true,
    groups: true,
    rules: true,
    commands: true,
  });

  /** ========================
   *  SIDEBAR SETTINGS
   *  ======================== */
  type SidebarSettings = {
    faqs: boolean;
    rules: boolean;
    groups: boolean;
    adBlock: boolean;
    devices: boolean;
    filters: boolean;
    routers: boolean;
    activity: boolean;
    callLogs: boolean;
    agentStatus: boolean;
    approveDevices: boolean;
    threatProtection: boolean;
  };

  const [sidebarSettings, setSidebarSettings] = useState<SidebarSettings>({
    faqs: true,
    rules: true,
    groups: true,
    adBlock: true,
    devices: true,
    filters: true,
    routers: true,
    activity: true,
    callLogs: true,
    agentStatus: true,
    approveDevices: true,
    threatProtection: true,
  });

  // Keep a reference of the last sidebar settings to detect changes
  const prevSidebarSettingsRef = useRef<SidebarSettings>(sidebarSettings);

  // Menu list for tabs
  const menuList = ['Dashboard', 'Account Settings', 'Sidebar Settings', 'Advanced'];

  /** ========================
   *  TOKEN + FETCHING
   *  ======================== */
  async function tokenCheck() {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const ID = payload.operatorId;
        setCustomerId(ID);
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }
  }

  useEffect(() => {
    if (token) tokenCheck();
  }, [token]);

  useEffect(() => {
    if (customerId) {
      fetchSettings();
      fetchSidebarSettings();
    }
  }, [customerId]);

  /** ========================
   *  FETCH & UPDATE UI SETTINGS
   *  ======================== */
  async function fetchSettings() {
    setIsLoading(true);
    try {
      const response = await axios.get(`/v2/op/master-operator/get-ui-settings?operatorId=${customerId}`);
      if (response?.data?.uiSettings) {
        setSettings(response.data.uiSettings);
      }
    } catch (error) {
      console.error('Error fetching UI settings:', error);
    } finally {
      setIsLoading(false);
    }
  }

  type SettingsKey = keyof Settings;
  const handleToggle = async (key: SettingsKey) => {
    const updated = {
      ...settings,
      [key]: !settings[key],
    };
    
    setSettings(updated);
    
    try {
      await axios.post(`/v2/op/master-operator/update-ui-settings?operatorId=${customerId}`, updated);
    } catch (error) {
      console.error('Error updating UI settings:', error);
      // Revert on error
      setSettings(prev => ({...prev, [key]: !updated[key]}));
    }
  };

  /** ========================
   *  FETCH & UPDATE SIDEBAR SETTINGS
   *  ======================== */
  async function fetchSidebarSettings() {
    try {
      const response = await axios.get(
        `/v2/op/master-operator/get-sidebar-settings?operatorId=${customerId}`
      );
      if (response?.data?.fetchSidebarSettings) {
        setSidebarSettings(response.data.fetchSidebarSettings);
        prevSidebarSettingsRef.current = response.data.fetchSidebarSettings;
      }
    } catch (error) {
      console.error('Error fetching sidebar settings:', error);
    }
  }

  type SidebarKey = keyof SidebarSettings;
  const handleSidebarToggle = async (key: SidebarKey) => {
    const updated = {
      ...sidebarSettings,
      [key]: !sidebarSettings[key],
    };

    setSidebarSettings(updated);

    const previousValue = prevSidebarSettingsRef.current[key];
    const newValue = updated[key];

    if (previousValue !== newValue) {
      try {
        await axios.post(`/v2/op/master-operator/update-sidebar-settings?operatorId=${customerId}`, updated);
        prevSidebarSettingsRef.current = updated;
      } catch (error) {
        console.error('Error updating sidebar settings:', error);
        // Revert on error
        setSidebarSettings(prev => ({...prev, [key]: !updated[key]}));
      }
    }
  };

  /** ========================
   *  BATCH OPERATIONS
   *  ======================== */
  const toggleAllSettings = async (enable: boolean) => {
    const updated = Object.fromEntries(
      Object.keys(settings).map(key => [key, enable])
    ) as Settings;
    
    setSettings(updated);
    
    try {
      await axios.post(`/v2/op/master-operator/update-ui-settings?operatorId=${customerId}`, updated);
    } catch (error) {
      console.error('Error batch updating UI settings:', error);
      fetchSettings(); // Revert by fetching original settings
    }
  };

  const toggleAllSidebarSettings = async (enable: boolean) => {
    const updated = Object.fromEntries(
      Object.keys(sidebarSettings).map(key => [key, enable])
    ) as SidebarSettings;
    
    setSidebarSettings(updated);
    
    try {
      await axios.post(`/v2/op/master-operator/update-sidebar-settings?operatorId=${customerId}`, updated);
      prevSidebarSettingsRef.current = updated;
    } catch (error) {
      console.error('Error batch updating sidebar settings:', error);
      fetchSidebarSettings(); // Revert by fetching original settings
    }
  };

  /** ========================
   *  VIEW MODEL
   *  ======================== */
  const countOn = (obj: Record<string, boolean>) => Object.values(obj).filter(Boolean).length;
  const uiOn = countOn(settings);
  const uiTotal = Object.keys(settings).length;
  const sideOn = countOn(sidebarSettings);
  const sideTotal = Object.keys(sidebarSettings).length;
  const allUiOn = uiOn === uiTotal;
  const allSideOn = sideOn === sideTotal;
  const percent = (on: number, total: number) => (total ? Math.round((on / total) * 100) : 0);

  type ToggleItem<K extends string> = { key: K; title: string; subtitle: string; icon: LucideIcon };

  const accountGroups: { title: string; subtitle: string; icon: LucideIcon; items: ToggleItem<SettingsKey>[] }[] = [
    {
      title: 'Account Features',
      subtitle: 'Core account management capabilities',
      icon: UserCog,
      items: [
        { key: 'passwordManagement', title: 'Password Management', subtitle: 'Allow users to change and reset their password', icon: KeyRound },
        { key: 'alternateEmail', title: 'Alternate Email', subtitle: 'Allow users to manage a secondary email address', icon: MailPlus },
        { key: 'registration', title: 'Registration', subtitle: 'Enable device registration process', icon: UserPlus },
      ],
    },
    {
      title: 'Device Control',
      subtitle: 'Device management and control features',
      icon: Smartphone,
      items: [
        { key: 'details', title: 'Details', subtitle: 'Device information', icon: Info },
        { key: 'otp', title: 'OTP', subtitle: 'Registration OTP', icon: Hash },
        { key: 'pause', title: 'Pause', subtitle: 'Internet access control', icon: PauseCircle },
        { key: 'extraTime', title: 'Extra Time', subtitle: 'Rule exceptions', icon: Timer },
      ],
    },
    {
      title: 'Advanced Device Features',
      subtitle: 'Metrics, threat detail, profiles and enrolment',
      icon: BarChart3,
      items: [
        { key: 'metrics', title: 'Metrics', subtitle: 'QoE metrics display', icon: Activity },
        { key: 'threats', title: 'Threats', subtitle: 'Threat details', icon: ShieldAlert },
        { key: 'profile', title: 'Profile', subtitle: 'Age profile application', icon: Baby },
        { key: 'enroll', title: 'Enroll', subtitle: 'MDM enrollment', icon: Smartphone },
      ],
    },
    {
      title: 'Management & Administration',
      subtitle: 'Groups, rules and MDM commands',
      icon: Briefcase,
      items: [
        { key: 'groups', title: 'Groups', subtitle: 'Device group management', icon: Users },
        { key: 'rules', title: 'Rules', subtitle: 'Device rule management', icon: ListChecks },
        { key: 'commands', title: 'Commands', subtitle: 'MDM command interface', icon: Terminal },
      ],
    },
  ];

  const sidebarMeta: Record<SidebarKey, { subtitle: string; icon: LucideIcon }> = {
    faqs: { subtitle: 'Frequently asked questions section', icon: HelpCircle },
    rules: { subtitle: 'Network and access rules management', icon: ListChecks },
    groups: { subtitle: 'Device and user groups management', icon: Users },
    adBlock: { subtitle: 'Advertisement blocking controls', icon: Ban },
    devices: { subtitle: 'Connected devices list and management', icon: MonitorSmartphone },
    filters: { subtitle: 'Content filtering rules and settings', icon: Filter },
    routers: { subtitle: 'Network router configuration', icon: Router },
    activity: { subtitle: 'User activity logs and monitoring', icon: Activity },
    callLogs: { subtitle: 'Telephony call records and history', icon: PhoneCall },
    agentStatus: { subtitle: 'Agent connection and status monitoring', icon: Bot },
    approveDevices: { subtitle: 'Device approval requests and management', icon: BadgeCheck },
    threatProtection: { subtitle: 'Security threat monitoring and alerts', icon: ShieldCheck },
  };
  const sidebarTitle = (key: string) => key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');

  const tabIcons: Record<string, LucideIcon> = {
    Dashboard: LayoutDashboard,
    'Account Settings': Settings,
    'Sidebar Settings': Menu,
    Advanced: Wrench,
  };

  const enableEverything = () => {
    toggleAllSettings(true);
    toggleAllSidebarSettings(true);
  };
  const disableEverything = () => {
    toggleAllSettings(false);
    toggleAllSidebarSettings(false);
  };

  // Toggle row — same behaviour as the old CardWidget: run the handler, then toast.
  // Plain render functions (not inner components) so rows keep focus across re-renders.
  const toggleRow = ({
    key,
    title,
    subtitle,
    icon: Icon,
    checked,
    onToggle,
  }: {
    key: string;
    title: string;
    subtitle: string;
    icon: LucideIcon;
    checked: boolean;
    onToggle: () => void;
  }) => (
    <div key={key} className={`ui-toggle ${checked ? 'on' : ''}`}>
      <span className="ui-toggle-icon">
        <Icon size={16} strokeWidth={1.9} />
      </span>
      <div className="ui-toggle-text">
        <span className="ui-toggle-title">{title}</span>
        <span className="bd-meta">{subtitle}</span>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={title}
        className={`bd-switch ${checked ? 'on' : ''}`}
        onClick={() => {
          onToggle();
          showToastMessage(`${title} ${checked ? 'disabled' : 'enabled'} successfully!`);
        }}
      />
    </div>
  );

  const statTile = ({ label, on, total, note }: { label: string; on: number; total: number; note: string }) => (
    <div key={label} className="ui-stat">
      <span className="bd-field-label">{label}</span>
      <span className="bd-stat-num ui-stat-num">
        {on}
        <span className="ui-stat-of">/ {total}</span>
      </span>
      <div className="bd-progress">
        <i style={{ width: `${percent(on, total)}%` }} />
      </div>
      <span className="bd-meta">{note}</span>
    </div>
  );

  /** ========================
   *  RENDER
   *  ======================== */
  return (
    <section id="ui-settings" className="ui-settings">
      {/* === HEADER === */}
      <header className="ui-hd">
        <span className="ui-hd-icon">
          <SlidersHorizontal size={20} />
        </span>
        <div className="ui-hd-text">
          <h2 className="bd-h2">User Interface Controls</h2>
          <span className="bd-body">Configure which features and interface elements are visible to end users.</span>
        </div>
      </header>

      {/* Loading State */}
      {isLoading ? (
        <LoadingSpinner text="Loading UI settings..." />
      ) : (
        <>
          {/* Section tabs */}
          <div className="bd-tabs ui-tabs" role="tablist" aria-label="User interface control sections">
            {menuList.map((item) => {
              const Icon = tabIcons[item];
              const active = selectedMenu === item;
              return (
                <button
                  key={item}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setSelectedMenu(item)}
                  className={`ui-tab ${active ? 'active' : ''}`}
                >
                  {Icon && <Icon size={15} strokeWidth={1.9} />}
                  {item}
                </button>
              );
            })}
          </div>

          {/* Dashboard Tab */}
          {selectedMenu === 'Dashboard' && (
            <div className="ui-panel">
              <div className="ui-stats">
                {statTile({ label: 'UI features enabled', on: uiOn, total: uiTotal, note: 'Account & device features' })}
                {statTile({ label: 'Sidebar items visible', on: sideOn, total: sideTotal, note: 'End-user navigation' })}
{statTile({ label: 'Total elements active', on: uiOn + sideOn, total: uiTotal + sideTotal, note: 'Combined UI and sidebar' })}
              </div>

              <div className="ui-cards-2">
                <div className="ui-card">
                  <div className="ui-card-hd">
                    <h3 className="bd-h3">Quick actions</h3>
                    <span className="bd-meta">Quickly enable or disable all settings</span>
                  </div>
                  <div className="ui-card-actions">
                    <button type="button" className="bd-btn primary" onClick={enableEverything}>
                      <CheckCircle size={15} />
                      Enable All
                    </button>
                    <button type="button" className="bd-btn danger-soft" onClick={disableEverything}>
                      <XCircle size={15} />
                      Disable All
                    </button>
                  </div>
                </div>

                <div className="ui-card">
                  <div className="ui-card-hd">
                    <h3 className="bd-h3">Navigation</h3>
                    <span className="bd-meta">Jump to specific settings sections</span>
                  </div>
                  <div className="ui-card-actions">
                    <button type="button" className="bd-btn secondary" onClick={() => setSelectedMenu('Account Settings')}>
                      <Settings size={15} />
                      Account Settings
                    </button>
                    <button type="button" className="bd-btn secondary" onClick={() => setSelectedMenu('Sidebar Settings')}>
                      <Menu size={15} />
                      Sidebar Settings
                    </button>
                  </div>
                </div>
              </div>

              <div className="ui-card">
                <div className="ui-card-hd">
                  <h3 className="bd-h3">Settings status</h3>
                </div>
                <div className="ui-status-list">
                  {[
                    { title: 'Account Features', icon: UserCog, on: uiOn, total: uiTotal, verb: 'enabled', label: 'Active' },
                    { title: 'Sidebar Items', icon: Menu, on: sideOn, total: sideTotal, verb: 'visible', label: 'Visible' },
                  ].map(({ title, icon: Icon, on, total, verb, label }) => (
                    <div key={title} className="ui-status">
                      <span className="ui-toggle-icon">
                        <Icon size={16} />
                      </span>
                      <div className="ui-toggle-text">
                        <span className="ui-toggle-title">{title}</span>
                        <span className="bd-meta">
                          {on} of {total} {verb}
                        </span>
                      </div>
                      <span className={`bd-chip ${on > total / 2 ? 'mint' : 'warn'}`}>
                        {percent(on, total)}% {label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Account Settings Tab */}
          {selectedMenu === 'Account Settings' && (
            <div className="ui-panel">
              <div className="ui-section-hd">
                <div className="ui-card-hd">
                  <h3 className="bd-h3">Account Management</h3>
                  <span className="bd-meta">Configure features for end users to manage their accounts</span>
                </div>
                <div className="ui-section-tools">
                  <span className="bd-chip mint">
                    <span className="bd-mono">
                      {uiOn} / {uiTotal}
                    </span>
                    enabled
                  </span>
                  <button type="button" className="bd-btn secondary ui-btn-sm" onClick={() => toggleAllSettings(!allUiOn)}>
                    {allUiOn ? 'Disable All' : 'Enable All'}
                  </button>
                </div>
              </div>

              <div className="ui-groups">
                {accountGroups.map(({ title, subtitle, icon: Icon, items }) => {
                  const groupOn = items.filter((item) => settings[item.key]).length;
                  return (
                    <div key={title} className="ui-card ui-group h-full">
                      <div className="ui-group-hd">
                        <span className="ui-hd-icon sm">
                          <Icon size={16} />
                        </span>
                        <div className="ui-card-hd">
                          <h4 className="bd-h3">{title}</h4>
                          <span className="bd-meta">{subtitle}</span>
                        </div>
                        <span className="bd-chip bd-mono">
                          {groupOn}/{items.length}
                        </span>
                      </div>
                      <div className="ui-toggle-list">
                        {items.map((item) => (
                          toggleRow({
                            key: item.key,
                            title: item.title,
                            subtitle: item.subtitle,
                            icon: item.icon,
                            checked: settings[item.key],
                            onToggle: () => handleToggle(item.key),
                          })
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sidebar Settings Tab */}
          {selectedMenu === 'Sidebar Settings' && (
            <div className="ui-panel">
              <div className="ui-section-hd">
                <div className="ui-card-hd">
                  <h3 className="bd-h3">Sidebar Navigation</h3>
                  <span className="bd-meta">
                    Control visibility of sidebar menu sections · {sideOn} of {sideTotal} menu items are visible
                  </span>
                </div>
                <div className="ui-section-tools">
                  <span className="bd-chip mint">
                    <span className="bd-mono">
                      {sideOn} / {sideTotal}
                    </span>
                    visible
                  </span>
                  <button
                    type="button"
                    className="bd-btn secondary ui-btn-sm"
                    onClick={() => toggleAllSidebarSettings(!allSideOn)}
                  >
                    {allSideOn ? <EyeOff size={14} /> : <Eye size={14} />}
                    {allSideOn ? 'Hide All' : 'Show All'}
                  </button>
                </div>
              </div>

              <div className="ui-sidebar-grid">
                {(Object.keys(sidebarSettings) as SidebarKey[]).map((key) => (
                  toggleRow({
                    key,
                    title: sidebarTitle(key),
                    subtitle: sidebarMeta[key]?.subtitle || 'Toggle visibility in sidebar menu',
                    icon: sidebarMeta[key]?.icon || Menu,
                    checked: sidebarSettings[key],
                    onToggle: () => handleSidebarToggle(key),
                  })
                ))}
              </div>
            </div>
          )}

          {/* Advanced Tab */}
          {selectedMenu === 'Advanced' && (
            <div className="ui-panel">
              <div className="ui-card-hd">
                <h3 className="bd-h3">Advanced Settings</h3>
                <span className="bd-meta">Advanced configuration options and bulk operations</span>
              </div>

              {/* Bulk Operations */}
              <div className="ui-card">
                <div className="ui-card-hd">
                  <h4 className="bd-h3">Bulk operations</h4>
                  <span className="bd-meta">Perform bulk operations on all settings</span>
                </div>
                <div className="ui-bulk">
                  <button type="button" className="ui-bulk-tile ok" onClick={enableEverything}>
                    <CheckCircle size={20} />
                    <span className="ui-toggle-text">
                      <span className="ui-toggle-title">Enable Everything</span>
                      <span className="bd-meta">Enable all UI features and sidebar items</span>
                    </span>
                  </button>
                  <button type="button" className="ui-bulk-tile danger" onClick={disableEverything}>
                    <XCircle size={20} />
                    <span className="ui-toggle-text">
                      <span className="ui-toggle-title">Disable Everything</span>
                      <span className="bd-meta">Disable all UI features and sidebar items</span>
                    </span>
                  </button>
                </div>
              </div>

              {/* Export/Import */}
              <div className="ui-card">
                <div className="ui-card-hd">
                  <h4 className="bd-h3">Export &amp; import</h4>
                  <span className="bd-meta">Export current settings or import from a file</span>
                </div>
                <div className="ui-card-actions start">
                  <button
                    type="button"
                    className="bd-btn primary"
                    onClick={() => {
                      const data = {
                        uiSettings: settings,
                        sidebarSettings: sidebarSettings,
                        exportedAt: new Date().toISOString(),
                        operatorId: customerId,
                      };
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `ui-settings-${customerId}-${new Date().toISOString().split('T')[0]}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    <Download size={15} />
                    Export Settings
                  </button>
                  <button
                    type="button"
                    className="bd-btn secondary"
                    onClick={() => {
                      alert('Import functionality would open a file picker in a real implementation');
                    }}
                  >
                    <Upload size={15} />
                    Import Settings
                  </button>
                </div>
              </div>

              {/* Reset Section */}
              <div className="ui-card">
                <div className="ui-card-hd">
                  <h4 className="bd-h3">Reset options</h4>
                  <span className="bd-meta">Reset settings to their default values</span>
                </div>
                <div className="ui-reset-list">
                  <button
                    type="button"
                    className="ui-reset"
                    onClick={() => {
                      if (window.confirm('Reset all UI settings to defaults?')) {
                        const defaults = {
                          passwordManagement: true,
                          alternateEmail: true,
                          registration: true,
                          details: true,
                          otp: true,
                          pause: true,
                          extraTime: true,
                          metrics: true,
                          threats: true,
                          profile: true,
                          enroll: true,
                          groups: true,
                          rules: true,
                          commands: true,
                        };
                        setSettings(defaults);
                        axios.post(`/v2/op/master-operator/update-ui-settings?operatorId=${customerId}`, defaults);
                      }
                    }}
                  >
                    <span>Reset UI Settings to Defaults</span>
                    <RotateCcw size={15} />
                  </button>
                  <button
                    type="button"
                    className="ui-reset"
                    onClick={() => {
                      if (window.confirm('Reset all sidebar settings to defaults?')) {
                        const defaults = {
                          faqs: true,
                          rules: true,
                          groups: true,
                          adBlock: true,
                          devices: true,
                          filters: true,
                          routers: true,
                          activity: true,
                          callLogs: true,
                          agentStatus: true,
                          approveDevices: true,
                          threatProtection: true,
                        };
                        setSidebarSettings(defaults);
                        axios.post(`/v2/op/master-operator/update-sidebar-settings?operatorId=${customerId}`, defaults);
                      }
                    }}
                  >
                    <span>Reset Sidebar Settings to Defaults</span>
                    <RotateCcw size={15} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
};
